import "./analytics";
import { driver } from "driver.js";
import React, { Component, lazy, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import ProtectedRoute from "@components/ProtectedRoute/ProtectedRoute";
import UserLanguageLocalStorage from "@components/SwitchLanguage/constants/UserLanguageLocalStorage";
import SwitchOrganisationModal from "@components/SwitchOrganisationModal/SwitchOrganisationModal";
import { Home } from "@constants/tours/Home";
import TranslationPrefix from "@constants/TranslationPrefix";
import { FeatureFlagsAccessProvider } from "@context/FeatureFlagsAccessContext/FeatureFlagsAccessContext";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faBlackTie, faFacebookF, faLinkedinIn } from "@fortawesome/free-brands-svg-icons";
import {
    faStar,
    faStarHalf,
    faBell,
    faFilePdf,
    faChartBar,
    faEnvelope as envelope,
    faClock,
    faCalendar,
    faCalendarCheck,
} from "@fortawesome/free-regular-svg-icons";
import {
    faSpinner,
    faXmark,
    faUserFriends,
    faBuilding,
    faUser,
    faUsers,
    faUsersCog,
    faUserSecret,
    faAngleDown,
    faSignOutAlt,
    faPrint,
    faDesktop,
    faUserEdit,
    faArrowLeft,
    faArrowRight,
    faTimes,
    faInfoCircle,
    faTachometerAlt,
    faPlusCircle,
    faChevronDown,
    faExpandArrowsAlt,
    faStar as faStarSolid,
    faTimesCircle,
    faCheck,
    faAngleUp,
    faClipboardList,
    faCubes,
    faFile,
    faHeart,
    faPlusSquare,
    faChevronUp,
    faFolderOpen,
    faFolder,
    faChevronRight,
    faSortDown,
    faPlus,
    faCaretDown,
    faCaretRight,
    faExclamationTriangle,
    faEnvelope,
    faChalkboardTeacher,
    faCheckCircle,
    faWarehouse,
    faSuitcase,
    faFileInvoiceDollar,
    faFileCsv,
    faTrashAlt,
    faSitemap,
    faPenFancy,
    faStickyNote,
    faCalculator,
    faBrain,
    faExclamationCircle,
    faEllipsis,
    faRedoAlt,
    faMountain,
    faGem,
    faBullhorn,
    faFire,
    faTheaterMasks,
    faBriefcase,
    faFileAlt,
    faPoll,
    faCity,
    faUndo,
    faStopwatch,
    faClone,
    faPen,
    faClipboard,
    faTasks,
    faBookReader,
    faMask,
    faHammer,
    faSave,
    faSort,
    faCodeBranch,
    faBalanceScale,
    faSquare,
    faHome,
    faListUl,
    faPhoneAlt,
    faChartLine,
    faRankingStar,
    faPeopleRoof,
    faMagnifyingGlassChart,
    faEnvelopesBulk,
    faChartColumn,
    faPeopleGroup,
    faTriangleExclamation,
    faX,
    faSortUp,
    faChartSimple,
    faEye,
    faArrowDown,
    faArrowUp,
    faHashtag,
    faPencil,
    faChevronLeft,
    faShare,
    faCircle,
    faUserPlus,
    faFileArrowDown,
    faHatWizard,
    faTable,
    faCopy,
    faCircleQuestion,
    faBullseye,
    faLocationDot,
    faFilePen,
    faMagnifyingGlass,
    faSquareFull,
    faFileSignature,
    faSquareMinus,
    faFileCircleXmark,
    faCog,
    faPersonCircleQuestion,
    faPersonCircleMinus,
    faPersonCircleXmark,
    faPersonCircleCheck,
    faIcons,
    faSpa,
    faWandMagicSparkles,
    faStreetView,
    faGraduationCap,
    faCompass,
    faCloudSun,
} from "@fortawesome/free-solid-svg-icons";
import { createTheme, ThemeProvider } from "@mui/material";
import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";
import RedirectRenderer from "@utils/RedirectRenderer";
import ApiFactory from "neon-frontend-utils/src/api/factory";
import NeonAuthenticator from "neon-frontend-utils/src/authenticator/neon";
import NeonAuthoriser from "neon-frontend-utils/src/authoriser/neon";
import Alert from "neon-frontend-utils/src/components/Alert";
// The authenticated route and component are dependent on the neon api instance
import Authenticated from "neon-frontend-utils/src/components/Authenticated";
import Authorised from "neon-frontend-utils/src/components/Authorised";
import MaintenanceNotificationBar from "neon-frontend-utils/src/components/MaintenanceNotificationBar/MaintenanceNotificationBar";
import Logger from "neon-frontend-utils/src/logger";
import translator from "neon-frontend-utils/src/translator";
// import common css so it becomes available in all page components and eases separation for client specific css.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import style from "../style/global.scss"; // eslint-disable-line no-unused-vars
import AppConfig from "./App.config";
import Menu from "./components/Menu";
import Components from "./constants/Components";
import { identifyUser } from "./services/analytics/analytics-events";
import store from "./store";

if (AppConfig.sentry.environments.includes(process.env.TARGET_ENV)) {
    const dsn = process.env.TARGET_ENV === "development" ? AppConfig.sentry.developDsn : AppConfig.sentry.dsn;

    if (dsn) {
        Sentry.init({
            dsn,
            environment: process.env.TARGET_ENV,
            integrations: [new Integrations.BrowserTracing()],
            release: process.env.SENTRY_RELEASE,
            tracesSampleRate: AppConfig.sentry.sampleRate,
        });
    }
}

// init logger
const logger = new Logger(AppConfig.logger);

logger.notice({
    component: Components.APPLICATION,
    message: "Open application",
});

// add imported icons to global library to make them available wherever the FontAwesomeIcon component is imported
library.add(
    faSpinner,
    faUserFriends,
    faBuilding,
    faUser,
    faUsers,
    faUsersCog,
    faCog,
    faUserSecret,
    faAngleDown,
    faXmark,
    faFacebookF,
    faLinkedinIn,
    faPrint,
    faDesktop,
    faUserEdit,
    faArrowLeft,
    faArrowRight,
    faTimes,
    faInfoCircle,
    faTachometerAlt,
    faStar,
    faPlusSquare,
    faChevronUp,
    faFolderOpen,
    faFolder,
    faChevronRight,
    faChevronDown,
    faMask,
    faSortDown,
    faPlus,
    faCaretDown,
    faCaretRight,
    faStar,
    faPlusCircle,
    faExpandArrowsAlt,
    faStarSolid,
    faStarHalf,
    faSort,
    faFacebookF,
    faLinkedinIn,
    faTimesCircle,
    faCheck,
    faAngleUp,
    faClipboardList,
    faCubes,
    faFile,
    faHeart,
    faEnvelope,
    faChalkboardTeacher,
    faCheckCircle,
    faExclamationTriangle,
    faWarehouse,
    faSuitcase,
    faBell,
    faFileInvoiceDollar,
    faFileCsv,
    faFileCircleXmark,
    envelope,
    faPenFancy,
    faTrashAlt,
    faFilePdf,
    faStickyNote,
    faSitemap,
    faCalculator,
    faBrain,
    faBullseye,
    faSignOutAlt,
    faChartBar,
    faExclamationCircle,
    faCity,
    faFileAlt,
    faEllipsis,
    faPoll,
    faUndo,
    faRedoAlt,
    faMountain,
    faGem,
    faBullhorn,
    faBlackTie,
    faFire,
    faTheaterMasks,
    faBriefcase,
    faStopwatch,
    faClone,
    faPen,
    faClipboard,
    faTasks,
    faBookReader,
    faHammer,
    faSave,
    faSquareMinus,
    faCodeBranch,
    faBalanceScale,
    faSquare,
    faClock,
    faHome,
    faListUl,
    faPhoneAlt,
    faChartLine,
    faRankingStar,
    faCalendar,
    faCalendarCheck,
    faPeopleRoof,
    faEnvelopesBulk,
    faChartColumn,
    faPeopleGroup,
    faTriangleExclamation,
    faX,
    faSortUp,
    faChartSimple,
    faEye,
    faHashtag,
    faArrowDown,
    faArrowUp,
    faMagnifyingGlass,
    faPencil,
    faChevronLeft,
    faShare,
    faCircle,
    faUserPlus,
    faFileArrowDown,
    faHatWizard,
    faTable,
    faCopy,
    faCircleQuestion,
    faLocationDot,
    faFilePen,
    faMagnifyingGlassChart,
    faSquareFull,
    faFileSignature,
    faPersonCircleQuestion,
    faPersonCircleMinus,
    faPersonCircleXmark,
    faPersonCircleCheck,
    faIcons,
    faSpa,
    faWandMagicSparkles,
    faStreetView,
    faGraduationCap,
    faCompass,
    faCloudSun,
);

// keep browser localStorage in sync with the featureToggles state
store.subscribe(() => {
    localStorage.setItem("featureToggles", JSON.stringify(store.getState().featureToggleReducer.featureToggles));
});

// configure the Neon API once, so we can use it in any component from now
ApiFactory.create(
    "neon",
    AppConfig.api.neon,
    new NeonAuthenticator(AppConfig.authenticator.neon, AppConfig.authenticator.cognito),
    new NeonAuthoriser(AppConfig.authoriser.neon),
);
const api = ApiFactory.get("neon");

// configure API for ApiPlatform
ApiFactory.create(
    "neon-v2", // name
    AppConfig.api.neon, // apiConfig, for now it's merged with v1 because most props overlap
    new NeonAuthenticator(AppConfig.authenticator.neon, AppConfig.authenticator.cognito), // reusing authenticator
    new NeonAuthoriser(AppConfig.authoriser.neon), // reusing authoriser
    "apiPlatform",
);

const theme = createTheme({
    typography: {
        fontFamily: '"Corbel", "Arial"',
    },
});

/**
 * Renders the app
 * @returns {{}} app
 */
class App extends Component {
    constructor(props) {
        super(props);

        /* keep track of the user and whether to show the application menu overlay (for responsive views) */
        this.applicationState = {
            activeNode: null,
            maintenance: false,
            modalToSwitchOrganisation: false,
            mode: false,
            showMenuOverlay: true,
            user: null,
        };

        this.showMenu = this.showMenu.bind(this);
        this.hideMenu = this.hideMenu.bind(this);
        this.showModalToSwitchOrganisation = this.showModalToSwitchOrganisation.bind(this);
        this.closeModalToSwitchOrganisation = this.closeModalToSwitchOrganisation.bind(this);
    }

    componentDidMount() {
        this.applicationState.user = this.props.user;
        this.applicationState.maintenance = this.props.maintenance;
        this.setState(this.applicationState);

        identifyUser(this.props.user);
    }

    /**
     * Sets the general prop to indicate whether the application menu overlay should be visible
     * @returns {*} undefined
     */
    showMenu() {
        this.applicationState.showMenuOverlay = true;
        this.setState(this.applicationState);
    }

    /**
     * Sets the general prop to indicate whether the application menu overlay should be hidden
     * @param {Object} activeNode - activeNode to forward to OrganisationSettings component (since it is unmounted when menu is hidden)
     * @returns {*} undefined
     */
    hideMenu(activeNode) {
        this.applicationState.showMenuOverlay = false;
        this.applicationState.activeNode = activeNode || null;
        this.setState(this.applicationState);
    }

    showModalToSwitchOrganisation(mode) {
        this.applicationState.modalToSwitchOrganisation = true;
        this.applicationState.mode = mode;
        this.setState(this.applicationState);
    }

    closeModalToSwitchOrganisation() {
        this.applicationState.modalToSwitchOrganisation = false;
        this.setState(this.applicationState);
    }

    componentDidUpdate() {
        document.title = AppConfig.pageTitle;
    }

    /**
     * Dynamically loads a route component and optionally wraps it in a ProtectedRoute for authorization.
     *
     * @param {string} identifier - The identifier for the component to be dynamically imported.
     * @param {boolean} [authorisedRoute=false] - Whether the route should be protected and require authorization.
     * @param {Object} [forwardedProps={}] - Props to be forwarded to the dynamically imported component.
     * @returns {JSX.Element} A JSX element that either renders the protected or unprotected dynamic component.
     */
    getLazyRoute(identifier, authorisedRoute = false, forwardedProps = {}) {
        const DynamicComponent = lazy(() => import(`./pages/${identifier}`));

        const toCamelCase = (title) => title.charAt(0).toLowerCase() + title.slice(1);

        return (
            <Suspense>
                {authorisedRoute ? (
                    <ProtectedRoute api={api} componentName={toCamelCase(identifier)} redirectPath="/error">
                        <DynamicComponent {...forwardedProps} />
                    </ProtectedRoute>
                ) : (
                    <DynamicComponent {...forwardedProps} />
                )}
            </Suspense>
        );
    }

    render() {
        const { user, maintenance, maintenanceDate } = this.props;

        // if a preferred languageId was saved for this user, save it to localStorage. The switchLanguageReducer, used
        // on every page to retrieve the language id, always looks at localStorage first, applicationDefault second
        if (user?.language) {
            const localStorageValue = localStorage.getItem(UserLanguageLocalStorage.USER_LANGUAGE);
            const normalisedReceivedLanguageId = user.language.replace("-", "_");

            // retrieved languageId different from localStorage, persist it and refresh the page in the new language
            if (localStorageValue !== normalisedReceivedLanguageId) {
                localStorage.setItem(UserLanguageLocalStorage.USER_LANGUAGE, normalisedReceivedLanguageId);

                // reload the page in the new language
                RedirectRenderer.render(window.location.pathname, true, null);
            }
        }

        // maintenance is taking place, return the maintenance component
        if (maintenance) {
            return (
                <Provider store={store}>
                    <section id="layout">
                        <BrowserRouter>
                            <Routes>
                                <Route element={this.getLazyRoute("Maintenance", false)} path="*" />
                            </Routes>
                        </BrowserRouter>
                    </section>
                </Provider>
            );
        }

        // determine the default home page (in case "/" was attempted)
        const defaultPage = user?.roles.some((role) => AppConfig.authoriser.neon.home.route.includes(role))
            ? "/home"
            : "/participants";

        // maintenance is upcoming, prepare warning
        const maintenanceNotification = maintenanceDate ? (
            <MaintenanceNotificationBar
                languageId={
                    store?.getState()?.switchLanguageReducer?.languageId || AppConfig.languages.defaultLanguageId
                }
                maintenanceDate={maintenanceDate}
            />
        ) : null;

        const userLanguage = localStorage.getItem(UserLanguageLocalStorage.USER_LANGUAGE);

        const translateDriverSteps = (steps, translations) => {
            const stepsToShow = steps.filter((step) =>
                api.getAuthoriser().authorise(api.getAuthenticator().getUser(), step.component, step.action),
            );

            return stepsToShow.map((step) => ({
                ...step,
                popover: {
                    ...step.popover,
                    description: translations[step.popover.description] || step.popover.description,
                    title: translations[step.popover.title] || step.popover.title,
                },
            }));
        };

        const i18n = translator(userLanguage, [TranslationPrefix.TOUR]);

        const driverObj = driver({
            doneBtnText: i18n.tour_done,
            nextBtnText: i18n.tour_next,
            prevBtnText: i18n.tour_previous,
            showProgress: true,
            stagePadding: 0,
            steps: translateDriverSteps(Home, i18n),
        });

        return (
            <Provider store={store}>
                <ThemeProvider theme={theme}>
                    <FeatureFlagsAccessProvider>
                        <section id="layout">
                            <BrowserRouter>
                                <Authorised action={"viewMenu"} api={api} component={"menu"}>
                                    <Menu
                                        key="header"
                                        driverObj={driverObj}
                                        showMenu={this.showMenu}
                                        showModalToSwitchOrganisation={this.showModalToSwitchOrganisation}
                                        user={user}
                                    />
                                </Authorised>
                                {maintenanceNotification}
                                <Alert />
                                <Authenticated api={api}>
                                    <SwitchOrganisationModal
                                        closeModal={this.closeModalToSwitchOrganisation}
                                        isOpen={this.applicationState.modalToSwitchOrganisation}
                                        mode={this.applicationState.mode}
                                        user={user}
                                    />
                                </Authenticated>
                                <Routes>
                                    <Route element={<Navigate replace to={defaultPage} />} path="/" />
                                    <Route
                                        element={this.getLazyRoute("SelectOrganisation", false, {
                                            showModalToSwitchOrganisation: this.showModalToSwitchOrganisation,
                                        })}
                                        path="/login/select-organisation"
                                    />
                                    <Route element={this.getLazyRoute("Maintenance", false)} path="/maintenance" />
                                    <Route element={this.getLazyRoute("Login", false)} exact path="/login" />
                                    <Route
                                        element={this.getLazyRoute("MagicLink", false)}
                                        path="/magic-link/:linkHash"
                                    />
                                    <Route
                                        element={this.getLazyRoute("ForgotPassword", false)}
                                        path="/forgot-password"
                                    />
                                    <Route
                                        element={this.getLazyRoute("ExampleReport", false)}
                                        path="/:language?/example-report/:product/:reportSectionHandle"
                                    />
                                    <Route
                                        element={this.getLazyRoute("ExampleReport", false)}
                                        path="/:language?/example-report/:product"
                                    />
                                    <Route
                                        element={this.getLazyRoute("ExampleReport", false)}
                                        path="/example-reports/:reportSlug"
                                    />
                                    <Route
                                        element={this.getLazyRoute("ExampleReport", false)}
                                        path="/example-reports/:reportSlug/:reportSectionHandle"
                                    />
                                    <Route
                                        element={this.getLazyRoute("ExampleReports", true)}
                                        path="/example-reports"
                                    />

                                    <Route
                                        element={this.getLazyRoute("ForgotPassword", false)}
                                        path="/reset-password"
                                    />
                                    <Route
                                        element={this.getLazyRoute("Register", false)}
                                        path="/register/:accountHasRoleSlug?"
                                    />
                                    <Route element={this.getLazyRoute("FeatureToggles", true)} path="/featureToggles" />
                                    <Route element={this.getLazyRoute("CacheManager", true)} path="/cacheManager" />
                                    <Route
                                        element={this.getLazyRoute("PlanningDashboard", true)}
                                        path="/planningDashboard"
                                    />
                                    <Route element={this.getLazyRoute("Finance", true)} path="/finance" />
                                    <Route
                                        element={this.getLazyRoute("Home", true, {
                                            driverObj,
                                        })}
                                        path="/home"
                                    />
                                    <Route
                                        element={this.getLazyRoute("ParticipantsDetail", true)}
                                        path="/participant/:id"
                                    />
                                    <Route element={this.getLazyRoute("Participants", true)} path="/participants/*" />
                                    <Route
                                        element={this.getLazyRoute("OrganisationSettings", true, {
                                            activeNode: this.applicationState.activeNode,
                                            hideMenu: this.hideMenu,
                                            showMenuOverlay: this.applicationState.showMenuOverlay,
                                        })}
                                        path="/organisation-settings/*"
                                    />
                                    <Route element={this.getLazyRoute("Users", true)} path="/users/*" />
                                    <Route
                                        element={this.getLazyRoute("Report", true, {
                                            user,
                                        })}
                                        path="/report/:participantSessionSlug/:reportSectionHandle?"
                                    />
                                    <Route element={this.getLazyRoute("Profile", true)} path="/profile" />
                                    <Route
                                        element={this.getLazyRoute("Results", true)}
                                        path="/results/:participantSessionSlug"
                                    />
                                    <Route element={this.getLazyRoute("LtpSettings", true)} path="/ltpSettings/*" />
                                    <Route
                                        element={this.getLazyRoute("TalentAnalyticsDetail", true)}
                                        path="/talent-analytics/:id"
                                    />
                                    <Route
                                        element={this.getLazyRoute("TalentAnalytics", true)}
                                        path="/talent-analytics/"
                                    />
                                    <Route
                                        element={this.getLazyRoute("TalentAnalyticsSettings", true)}
                                        path="/talent-analytics/:id/settings"
                                    />
                                    <Route element={this.getLazyRoute("Teams", true)} path="/teams" />
                                    <Route
                                        element={this.getLazyRoute("TeamSettings", true)}
                                        path="/teams/:id/settings"
                                    />
                                    <Route element={this.getLazyRoute("TeamReport", true)} path="/teams/:id/report" />
                                    <Route element={this.getLazyRoute("Error", false)} path="/error" />
                                    <Route element={this.getLazyRoute("Error", false)} />
                                </Routes>
                            </BrowserRouter>
                        </section>
                    </FeatureFlagsAccessProvider>
                </ThemeProvider>
            </Provider>
        );
    }
}

const rootElement = document.getElementById("root");
const root = createRoot(rootElement);

// before rendering the app, always first fetch the current user (if available)
api.getAuthenticator()
    .refreshAndGetUser()
    .then((user) => {
        let maintenance = false;

        if (user.maintenance === true) {
            maintenance = true;
        }

        const maintenanceDate = api.getMaintenanceDate();

        root.render(<App maintenance={maintenance} maintenanceDate={maintenanceDate} user={user} />);
    })
    .catch(() => {
        root.render(<App />);
    });
